import { Transform } from 'class-transformer';
import {
	IsArray,
	IsBoolean,
	IsIn,
	IsNumber,
	IsObject,
	IsOptional,
	IsString,
	Matches,
	MaxLength,
} from 'class-validator';
import type { CustomFieldSurfacesModel } from '../../constants/custom-field-surfaces';
import { CUSTOM_FIELD_DATA_TYPES, type CustomFieldDataType } from '../types';

export const CUSTOM_FIELD_TYPES = [
	'text',
	'textarea',
	'number',
	'currency',
	'date',
	'datetime',
	'boolean',
	'select',
	'multiselect',
	'phone',
	'email',
	'url',
] as const;

export type CustomFieldType = (typeof CUSTOM_FIELD_TYPES)[number];

export const CUSTOM_FIELD_ENTITY_TYPES = [
	'client',
	'item',
	'vendor',
	'invoice',
	'order',
	'quote',
	'opportunity',
	'issue',
	'event',
	// PD-17106 — client visits (m_sm_client_visit.customFields).
	'visit',
] as const;

export type CustomFieldEntityType = (typeof CUSTOM_FIELD_ENTITY_TYPES)[number];

export function sanitizeFieldKey(input: string): string {
	return input.replace(/[^a-zA-Z0-9_]/g, '');
}

export class CreateCustomFieldDto {
	@IsString()
	@Transform(({ value }) => sanitizeFieldKey(value))
	@Matches(/^[a-zA-Z][a-zA-Z0-9_]{0,59}$/, {
		message:
			'Field key must start with a letter and contain only letters, numbers, and underscores (max 60 chars)',
	})
	@MaxLength(60)
	public fieldKey!: string;

	@IsString()
	@IsIn(CUSTOM_FIELD_ENTITY_TYPES)
	public entityType!: CustomFieldEntityType;

	@IsString()
	@MaxLength(255)
	public label!: string;

	@IsString()
	@IsIn(CUSTOM_FIELD_TYPES)
	public fieldType!: CustomFieldType;

	@IsString()
	@IsOptional()
	public defaultValue?: string | null;

	@IsBoolean()
	@IsOptional()
	public isRequired?: boolean;

	@IsObject()
	@IsOptional()
	public validationRules?: Record<string, unknown> | null;

	@IsArray()
	@IsNumber({}, { each: true })
	@IsOptional()
	public allowedRoles?: Array<number> | null;

	@IsNumber()
	@IsOptional()
	public sortOrder?: number;

	@IsArray()
	@IsOptional()
	public selectOptions?: Array<{ value: string; label: string }> | null;

	/**
	 * "Available for filtering" at creation time — same semantics as on
	 * UpdateCustomFieldDto. Offered on create so an admin can't populate
	 * values first and then wonder why they never appear as a facet.
	 */
	@IsBoolean()
	@IsOptional()
	public isIndexed?: boolean;

	/**
	 * Value semantics for matching and faceting (`fieldType` stays the UI
	 * control). Numeric kinds are parsed and converted into `unit` on write by
	 * ItemsService, so stored numbers are directly comparable. Omitted means
	 * `text`, which is a pass-through and the behavior every existing
	 * definition has.
	 */
	@IsString()
	@IsIn(CUSTOM_FIELD_DATA_TYPES)
	@IsOptional()
	public dataType?: CustomFieldDataType;

	/** Canonical unit for the numeric kinds, e.g. 'mm'. */
	@IsString()
	@MaxLength(32)
	@IsOptional()
	public unit?: string | null;

	/** Provenance key from a source catalog, for cross-catalog mapping. */
	@IsString()
	@MaxLength(64)
	@IsOptional()
	public taxonomyKey?: string | null;

	/**
	 * PD-17106 — visit fields only: ask this field during a visit debrief.
	 * Ignored for other entity types.
	 */
	@IsBoolean()
	@IsOptional()
	public askInDebrief?: boolean;

	/** PD-17106 — the debrief question wording; empty asks with the label. */
	@IsString()
	@MaxLength(500)
	@IsOptional()
	public debriefQuestion?: string | null;

	/**
	 * PD-15892 — extra screens the field is filled in on, e.g.
	 * `{ cartSidebar: true }` for an order field shown in the CRM cart sidebar.
	 * Keys and values are checked by the service
	 * (describeCustomFieldSurfacesError).
	 */
	@IsObject()
	@IsOptional()
	public surfaces?: CustomFieldSurfacesModel | null;
}

export class UpdateCustomFieldDto {
	@IsString()
	@MaxLength(255)
	@IsOptional()
	public label?: string;

	@IsString()
	@IsIn(CUSTOM_FIELD_TYPES)
	@IsOptional()
	public fieldType?: CustomFieldType;

	@IsBoolean()
	@IsOptional()
	public isActive?: boolean;

	@IsString()
	@IsOptional()
	public defaultValue?: string | null;

	@IsBoolean()
	@IsOptional()
	public isRequired?: boolean;

	@IsObject()
	@IsOptional()
	public validationRules?: Record<string, unknown> | null;

	@IsArray()
	@IsNumber({}, { each: true })
	@IsOptional()
	public allowedRoles?: Array<number> | null;

	@IsNumber()
	@IsOptional()
	public sortOrder?: number;

	@IsArray()
	@IsOptional()
	public selectOptions?: Array<{ value: string; label: string }> | null;

	/**
	 * "Available for filtering" (Model A): when true, the field is projected
	 * into the item search index (`cf_<key>`) and becomes exposable as a
	 * storefront/search facet. Only meaningful for `entityType: 'item'`.
	 * Values for EXISTING items appear after the next full item reindex; new
	 * item saves index the field immediately (the toggle path puts the typed
	 * mapping on the live index so nothing dynamic-maps wrongly). A fieldType
	 * change force-resets this to false — OpenSearch cannot remap a live
	 * field, so a re-enable + reindex is a deliberate step.
	 */
	@IsBoolean()
	@IsOptional()
	public isIndexed?: boolean;

	/**
	 * See CreateCustomFieldDto.dataType. Retyping an existing definition does
	 * NOT rewrite the values already stored — they normalize on their next
	 * write, and a reindex is needed before the facet reflects the new type.
	 */
	@IsString()
	@IsIn(CUSTOM_FIELD_DATA_TYPES)
	@IsOptional()
	public dataType?: CustomFieldDataType;

	/** Canonical unit for the numeric kinds, e.g. 'mm'. */
	@IsString()
	@MaxLength(32)
	@IsOptional()
	public unit?: string | null;

	/** Provenance key from a source catalog, for cross-catalog mapping. */
	@IsString()
	@MaxLength(64)
	@IsOptional()
	public taxonomyKey?: string | null;

	/**
	 * PD-17106 — visit fields only: ask this field during a visit debrief.
	 * Ignored for other entity types.
	 */
	@IsBoolean()
	@IsOptional()
	public askInDebrief?: boolean;

	/** PD-17106 — the debrief question wording; empty asks with the label. */
	@IsString()
	@MaxLength(500)
	@IsOptional()
	public debriefQuestion?: string | null;

	/** See CreateCustomFieldDto.surfaces. Omitted leaves the stored value as is. */
	@IsObject()
	@IsOptional()
	public surfaces?: CustomFieldSurfacesModel | null;
}

export interface CustomFieldDefinitionDto {
	id: number;
	fieldKey: string;
	entityType: string;
	label: string;
	fieldType: string;
	sqlColumnType: string;
	// Typed-attribute columns. Always returned so a write is verifiable by the
	// caller that made it (an import script, the admin UI, a test).
	dataType: CustomFieldDataType;
	unit: string | null;
	taxonomyKey: string | null;
	isIndexed: boolean;
	isActive: boolean;
	defaultValue: string | null;
	isRequired: boolean;
	validationRules: Record<string, unknown> | null;
	allowedRoles: Array<number> | null;
	sortOrder: number;
	selectOptions: Array<{ value: string; label: string }> | null;
	/** PD-17106 — visit fields: asked during a visit debrief. */
	askInDebrief: boolean;
	/** PD-17106 — the debrief question wording (null: ask with the label). */
	debriefQuestion: string | null;
	/** PD-15892 — extra screens the field appears on; null for none. */
	surfaces: CustomFieldSurfacesModel | null;
	ownerUserId: number;
	createdDate: Date;
	modifiedDate: Date;
}

export type GetCustomFieldsResponseDto =
	| {
			success: true;
			data: Array<CustomFieldDefinitionDto>;
	  }
	| {
			success: false;
			error: string;
	  };

export type GetCustomFieldResponseDto =
	| {
			success: true;
			data: CustomFieldDefinitionDto;
	  }
	| {
			success: false;
			error: string;
	  };

export type CreateCustomFieldResponseDto =
	| {
			success: true;
			data: CustomFieldDefinitionDto;
	  }
	| {
			success: false;
			error: string;
	  };

export type UpdateCustomFieldResponseDto =
	| {
			success: true;
			data: CustomFieldDefinitionDto;
	  }
	| {
			success: false;
			error: string;
	  };

export type DeleteCustomFieldResponseDto =
	| {
			success: true;
			message: string;
	  }
	| {
			success: false;
			error: string;
	  };

/**
 * Body of POST api/clients/bulk-actions/update-custom-field (PD-16595). Selection works
 * like the client list's other bulk actions: the page's ids, or `mode: 'all'` with the
 * list's filters, resolved on the server to the clients the caller may modify.
 */
export class BulkUpdateClientCustomFieldDto {
	@IsArray()
	@IsNumber({}, { each: true })
	@IsOptional()
	public clientIds?: Array<number>;

	@IsIn(['page', 'all'])
	@IsOptional()
	public mode?: 'page' | 'all';

	@IsObject()
	@IsOptional()
	public filters?: Record<string, unknown>;

	@IsString()
	@IsOptional()
	public searchTerm?: string;

	@IsString()
	@IsOptional()
	public viewListId?: string;

	// Same shape as a definition's key: the bulk job writes it into a JSON path.
	@IsString()
	@Matches(/^[a-zA-Z][a-zA-Z0-9_]{0,59}$/)
	@MaxLength(60)
	public fieldKey!: string;

	// Any JSON value, checked against the field's rules on the server.
	@IsOptional()
	public value?: unknown;
}
